// Auth state for the browser.
//
// The session token lives in an httpOnly cookie set by the backend — JS cannot
// read it. This module stores only the user record (for getUserId() and UI
// display) in localStorage, keyed separately from the anonymous UUID.
//
// Truth ladder:
//   1. localStorage cache  — instant, may be stale (cookie may have expired)
//   2. GET /auth/me        — authoritative; the cookie is the only credential
//                            the server trusts. RequirePro consults this on
//                            every guarded mount so a stale UI can't bypass
//                            the server check forever.
//
// Helpers:
//   refreshAuthUser()        — fetch /auth/me, sync localStorage, return user.
//   useAuthUser()            — react hook that does the above on mount.
//   signOut()                — POST /auth/signout, clear local cache, return.

import { useEffect, useState } from "react";
import { apiFetch, apiJson } from "./api";
import { STORAGE_AUTH, STORAGE_EVENTS } from "@/constants/storage";

const AUTH_USER_KEY = STORAGE_AUTH.user;

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role?: string; // "pro" | "ordinary"
  household_type?: string;
  cea_number?: string;
  agency?: string;
  email_verified?: boolean;
  google_linked?: boolean;
  google_email?: string;
}

export function getAuthUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function setAuthUser(user: AuthUser): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    // Notify same-tab listeners (storage event only fires for *other* tabs).
    window.dispatchEvent(new Event(STORAGE_EVENTS.authChanged));
  } catch {
    // localStorage quota exceeded — graceful degradation
  }
}

// clearAuth removes the user record from localStorage.
// The session cookie is cleared server-side (call POST /auth/signout or let it expire).
export function clearAuth(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(AUTH_USER_KEY);
    window.dispatchEvent(new Event(STORAGE_EVENTS.authChanged));
  } catch {
    // ignore
  }
}

export function isAuthenticated(): boolean {
  return getAuthUser() !== null;
}

// refreshAuthUser asks the server who we are. On 401 (cookie expired or
// missing) it clears the local cache so guards get a clean negative.
// Returns the canonical user, or null when unauthenticated.
export async function refreshAuthUser(): Promise<AuthUser | null> {
  try {
    const { user } = await apiJson<{ user: AuthUser }>("/auth/me");
    if (user) setAuthUser(user);
    return user ?? null;
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 401) {
      // Cookie expired — wipe the stale UI cache so guards redirect cleanly.
      clearAuth();
    }
    return null;
  }
}

// signOut posts to /auth/signout (clears the session cookie server-side),
// then wipes localStorage. Always resolves; never throws — a successful
// sign-out is what the UI cares about, not what the server returned.
export async function signOut(): Promise<void> {
  try {
    await apiFetch("/auth/signout", { method: "POST" });
  } catch {
    // ignore — clear local state regardless
  }
  clearAuth();
}

// useAuthUser is the React-friendly version: returns the cached user
// immediately (so the first render is not a flash-of-null), then revalidates
// against /auth/me in a background effect. Subscribes to auth_changed events
// so sign-in / sign-out from anywhere updates every consumer.
//
// `pending` is true only on the *initial* load when there's no cached user
// — once we have a value (even a stale one), pending stays false and the
// caller can render confidently.
export function useAuthUser(): { user: AuthUser | null; pending: boolean } {
  const [user, setUser] = useState<AuthUser | null>(() => getAuthUser());
  const [pending, setPending] = useState<boolean>(() => getAuthUser() === null);

  useEffect(() => {
    let alive = true;

    void refreshAuthUser().then((u) => {
      if (!alive) return;
      setUser(u);
      setPending(false);
    });

    const onChange = () => {
      if (!alive) return;
      setUser(getAuthUser());
    };
    window.addEventListener(STORAGE_EVENTS.authChanged, onChange);
    window.addEventListener("storage", (e) => {
      if (e.key === AUTH_USER_KEY) onChange();
    });
    return () => {
      alive = false;
      window.removeEventListener(STORAGE_EVENTS.authChanged, onChange);
    };
  }, []);

  return { user, pending };
}
